import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { withStyles } from "@material-ui/core";
import withHardReload from "../../hocs/withHardReload";

import { AssetImageGrid } from "./Asset/AssetImageGrid";
import { AssetDataTable } from "./Asset/AssetDataTable/AssetDataTable";
import { AssetDealMetric } from "./Asset/AssetDealMetric";

import { CardPageLayout } from "../../component-lib/CardPageLayout";
import { DEAL_STATUSES, DEAL_STAGES, UserDashboardHeroCard, InputSimpleDropdown, COMPANY_TYPES } from "@loanbossinc/component-library";
import { filterOutByStatus, filterByStage, filterDeadDeals, filterDealNamesByFund, sortByDateProp, filterDealNamesByCompany, filterDealNamesBySponsorCompany } from "./dashboard.filters";
import { AcquisitionsImageGrid } from "./Acquisitions/AcquisitionsImageGrid";
import { AcquisitionsDataTable } from "./Acquisitions/AcquisitionsDataTable";
import { getScreenPreferences, updateContactScreenPreferences } from '../../shared-utility/store/state/screenPreferences';
import { getPortfolioMetrics } from '../../shared-utility/store/state/portfolio';
import { getFundMetrics } from '../../shared-utility/store/state/fund';
import FundModal from 'modals/FundModal';
import DealModal from 'modals/DealModal';
import { DealPdfSummaryModal } from "modals/DealPdfSummaryModal";
import { mapCompanySelector } from "store/state/auth";
import {
  getDealNames
  , getDashboardDeals
  , getDashboardFundNames
  , updateDashboardFund
  , postDashboardFund
  , getDashboardDealPdfSummary
  , postDashboardDeal
  , postCompanyBannerImage
  , deleteCompanyBannerImage
  , postCompanyLogoImage
  , deleteCompanyLogoImage
  , getMaxDealsInPdfSummary
} from './Dashboard.store';
import { getCompany, getCompanyForUser, getSponsorCompanies } from 'store/state/companies';
import { getContactSettingAsInt, setContactSetting } from "../../shared-utility/store/utils/contactSettingHelper";
import { getEnvConfig } from "../../helpers/environment-config";
import { convertIntegerToGUID } from "helpers/guid";

const defaultAssetManagementGroups = [{ id: 0, canEdit: false, name: "All assets" }];
const defaultSponsorCompaniesList = [{ id: 0, canEdit: false, name: "All assets" }];
const portfolioViewMetric = "portfolio_view_metric";
const fundViewMetric = "fund_view_metric";

// modal names
const FUND_MODAL = 'FUND_MODAL';
const DEAL_MODAL = 'DEAL_MODAL';
const DEAL_PDF_SUMMARY_MODAL = 'DEAL_PDF_SUMMARY_MODAL';

const DASHBOARD_CONFIG_ITEM = "54b808af-0976-11eb-962b-00ff5660363b";
const DEFAULT_DASHBOARD_SELECTION = 0;
const defaultImageUrl = `/images/default-tile.png`;

class Dashboard extends Component {
  state = {
    selectedFilter: null
    , openModal: {
      name: null
      , data: null
    }
    , filteredDealNames: []
    , filteredDeals: []
    , filteredAcquisitionDealNames: []
    , filteredAcquisitionDeals: []
    , searchText: ''
    , metricSearchText: ''
    , assetManagementGroups: [...defaultAssetManagementGroups]
    , sponsorCompaniesList: [...defaultSponsorCompaniesList]
    , dealStatus: ''
    , hasBrokerAccess: false
    , acquisitionsDevelopmentGroups:
      [
        { id: 0, name: "All assets" }
        , { id: DEAL_STAGES.DUE_DILIGENCE, name: "Due Diligence" }
        , { id: DEAL_STAGES.BEST_AND_FINAL, name: "Best and Final" }
        , { id: DEAL_STAGES.BID_SUBMITTED, name: "Bid Submitted" }
        , { id: DEAL_STAGES.INVESTMENT_COMMITTEE, name: "Investment Committee" }
        , { id: DEAL_STAGES.UNDERWRITING, name: "Underwriting" }
        , { id: DEAL_STATUSES.DEAD, name: "Dead" }
        , { id: DEAL_STAGES.UNDER_REVIEW, name: "Under Review" }
      ]
    , viewingDealsOfValue: null
    , selectedFund: null
  }

  componentDidMount() {

    this.props.getDealNames();
    this.props.getDashboardDeals();
    this.props.getDashboardFundNames();
    this.props.getPortfolioMetrics();
    this.props.getCompanyForUser();
    this.props.getSponsorCompanies();
    this.props.getScreenPreferences({ screenType: portfolioViewMetric });
    this.props.getScreenPreferences({ screenType: fundViewMetric });
    if (this.props.authentication.isGuest) {
      const guestCompanyId = localStorage.getItem('guestCompanyId');
      this.props.getCompany({ id: guestCompanyId });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { dealNames, deals, funds, companies } = this.props;
    const { sponsorCompanies } = companies;
    const { selectedFilter, viewingDealsOfValue } = this.state;

    // NOTE: on initial load we need to populate the filteredDealNames with dealNames
    if (
      selectedFilter !== prevState.selectedFilter // if filter updated
      || deals !== prevProps.deals // when deals is updated / returned
      || dealNames !== prevProps.dealNames // when deal names is updated / returned
      || viewingDealsOfValue !== prevState.viewingDealsOfValue // when deal company is updated / returned
    ) this.updateFilters();


    if (viewingDealsOfValue !== prevState.viewingDealsOfValue) // when deal company is updated / returned
      this.props.getPortfolioMetrics({ companyId: viewingDealsOfValue });

    if (funds !== prevProps.funds || funds.length !== prevProps.funds.length) {
      const assetManagementGroups = [...defaultAssetManagementGroups];
      funds.forEach(fund => assetManagementGroups.push({ canEdit: true, ...fund }));
      this.setState({ assetManagementGroups });
    }

    if (sponsorCompanies !== prevProps.companies.sponsorCompanies || sponsorCompanies.length !== prevProps.companies.sponsorCompanies.length) {
      const sponsorCompaniesList = [...defaultSponsorCompaniesList];
      sponsorCompanies.map(sc => sponsorCompaniesList.push({ canEdit: false, ...sc }));
      this.setState({ sponsorCompaniesList });
    }

    const hasSelectedFilter = this.hasSelectedFilter(this.state);
    const dashboardType = this.getDashBoardType(this.state);
    const groupId = this.getGroupId(this.state);
    const prevStateFilterId = this.getGroupId(prevState);
    const shouldUpdateFundMetrics = (!prevStateFilterId && groupId) || !!prevStateFilterId && prevStateFilterId !== groupId;

    //make sure userDashboardType is updated on hero card selection
    if (hasSelectedFilter && dashboardType === 0 && shouldUpdateFundMetrics) {
      const hasBrokerAccess = this.hasBrokerAccess();
      this.props.getFundMetrics({ fundId: selectedFilter.group.id, hasBrokerAccess });
      this.updateFilters();
    }

    if (dashboardType != prevState.userDashboardType) {
      if (getContactSettingAsInt(DASHBOARD_CONFIG_ITEM, DEFAULT_DASHBOARD_SELECTION) !== dashboardType) {
        setContactSetting(DASHBOARD_CONFIG_ITEM, dashboardType, true);
      }
    }
  }

  // Helper commands
  hasBrokerAccess = () => {
    if (!this.props.authentication) return false;

    let companyTypes = this.props.authentication.companyTypes;
    if (this.props.authentication.isGuest) {
      if (!this.props.companies || !this.props.companies.company)
        return false;
      companyTypes = this.props.companies.company.companyTypes;
    }

    return companyTypes.map(ct => ct.name).includes('Broker');
  }

  getMenuOptions = () => {
    if (this.props.authentication.isGuest) return null;
    const dashboardType = this.getDashBoardType(this.state);

    const dealStatus = (!dashboardType || dashboardType === 0) ? DEAL_STATUSES.CLOSED : DEAL_STATUSES.PIPELINE;

    const canCreateNew = this.props.authentication.companyLevel !== "Secondary Sponsor";

    const brokerOption = [];

    if (canCreateNew) {
      brokerOption.push({ label: 'Create new deal', onClick: () => this.openNewDealModal(dealStatus) });
    }
    brokerOption.push({ label: 'Create new group', onClick: () => this.openFundModalNew() });

    const nonBrokerOptions = [
      { label: 'Download loan summaries', onClick: () => this.openDealPdfSummaryModal() }
    ];

    return this.hasBrokerAccess() ? brokerOption : [...brokerOption, ...nonBrokerOptions];
  }

  getDashBoardType = (state) => {
    const hasSelectedFilter = state.selectedFilter && !!state.selectedFilter;
    return !hasSelectedFilter ? null : state.selectedFilter.userDashboardType;
  }

  getGroupId = (state) => {
    const hasSelectedFilter = state.selectedFilter && state.selectedFilter && state.selectedFilter.group;
    return !hasSelectedFilter ? null : state.selectedFilter.group.id;
  }

  getShouldShowAll = () => !this.state.selectedFilter
    || !this.state.selectedFilter.group
    || !this.state.selectedFilter.group.id
    || this.state.selectedFilter.group.id == 0;

  hasSelectedFilter = (state) => state.selectedFilter
    && state.selectedFilter.group
    && !!state.selectedFilter.group.id;

  getMetrics = () => {
    const { portfolioMetrics, fundMetrics } = this.props;
    return this.getShouldShowAll() ? portfolioMetrics : fundMetrics;
  }

  getScreenPreferences = () => {
    const { screenPreferences } = this.props;
    return this.getShouldShowAll() ? screenPreferences[portfolioViewMetric] : screenPreferences[fundViewMetric];
  }

  getScreenType = () => {
    return this.getShouldShowAll() ? portfolioViewMetric : fundViewMetric
  }

  getInitialFilters = () => {
    let filteredAcquisitionDealNames = filterOutByStatus(this.props.dealNames, [DEAL_STATUSES.CLOSED, DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD]);
    filteredAcquisitionDealNames = sortByDateProp(filteredAcquisitionDealNames, 'targetedClosing');

    let filteredAcquisitionDeals = filterOutByStatus(this.props.deals, [DEAL_STATUSES.CLOSED, DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD]);
    filteredAcquisitionDeals = sortByDateProp(filteredAcquisitionDeals, 'targetedClosing');

    return {
      filteredDealNames: this.props.dealNames
      , filteredDeals: this.props.deals
      , filteredAcquisitionDealNames
      , filteredAcquisitionDeals
    }
  }

  // State update commands

  updateFilters = () => {
    let newState = this.getInitialFilters();
    const hasSelectedFilter = this.hasSelectedFilter(this.state);
    const shouldShowAllGroup = hasSelectedFilter && this.state.selectedFilter.group.id === 0;

    if (!shouldShowAllGroup && (hasSelectedFilter && !this.state.selectedFilter.userDashboardType) && !this.hasBrokerAccess()) {
      const groupId = this.getGroupId(this.state);
      newState.filteredDealNames = filterDealNamesByFund(this.props.dealNames, groupId);
      newState.filteredDeals = filterDealNamesByFund(this.props.deals, groupId);
    } else if (!shouldShowAllGroup && (hasSelectedFilter && !this.state.selectedFilter.userDashboardType) && this.hasBrokerAccess() && this.state.sponsorCompaniesList) {
      const sponsorCompanyId = this.getGroupId(this.state);
      newState.filteredDealNames = filterDealNamesBySponsorCompany(this.props.dealNames, sponsorCompanyId);
      newState.filteredDeals = filterDealNamesBySponsorCompany(this.props.deals, sponsorCompanyId);
    }

    if (!shouldShowAllGroup && hasSelectedFilter && this.state.selectedFilter.userDashboardType === 1 && !this.hasBrokerAccess()) {
      const dealStageId = this.state.selectedFilter.group.id;

      // DEAL_STATUSES.DEAD is not a STAGE so we need a different filter clause for it
      if (dealStageId !== DEAL_STATUSES.DEAD) {
        let filteredAcquisitionDealNames = filterByStage(this.props.dealNames, dealStageId);
        filteredAcquisitionDealNames = filterOutByStatus(filteredAcquisitionDealNames, [DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD]);
        newState.filteredAcquisitionDealNames = filteredAcquisitionDealNames;

        let filteredAcquisitionDeals = filterByStage(this.props.deals, dealStageId);
        filteredAcquisitionDeals = filterOutByStatus(filteredAcquisitionDeals, [DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD]);
        newState.filteredAcquisitionDeals = filteredAcquisitionDeals;
      }

      // DEAL_STATUSES.DEAD not making the assumption that this should be an else
      if (dealStageId === DEAL_STATUSES.DEAD) {
        newState.filteredAcquisitionDealNames = filterDeadDeals(this.props.dealNames);
        newState.filteredAcquisitionDeals = filterDeadDeals(this.props.deals);
      }
    } else if (!shouldShowAllGroup && hasSelectedFilter && this.state.selectedFilter.userDashboardType === 1 && this.hasBrokerAccess() && this.state.sponsorCompaniesList) {
      const sponsorCompanyId = this.getGroupId(this.state);
      let filteredAcquisitionDealNames = filterOutByStatus(this.props.dealNames, [DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD, DEAL_STATUSES.CLOSED]);
      newState.filteredAcquisitionDealNames = filterDealNamesBySponsorCompany(filteredAcquisitionDealNames, sponsorCompanyId);

      let filteredAcquisitionDeals = filterOutByStatus(this.props.deals, [DEAL_STATUSES.DEAD, DEAL_STATUSES.SOLD, DEAL_STATUSES.CLOSED]);
      newState.filteredAcquisitionDeals = filterDealNamesBySponsorCompany(filteredAcquisitionDeals, sponsorCompanyId);
    }

    if (this.props.dealCompanies && this.props.dealCompanies.length > 1 && this.state.viewingDealsOfValue && this.state.viewingDealsOfValue !== -1) {
      const dealNames = newState.filteredDealNames && newState.filteredDealNames.length > 0 ? newState.filteredDealNames : this.props.dealNames;
      newState.filteredDealNames = filterDealNamesByCompany(dealNames, this.state.viewingDealsOfValue);

      const deals = newState.filteredDeals && newState.filteredDeals.length > 0 ? newState.filteredDeals : this.props.deals;
      newState.filteredDeals = filterDealNamesByCompany(deals, this.state.viewingDealsOfValue);

      const acquisitionDealNames = newState.filteredAcquisitionDealNames && newState.filteredAcquisitionDealNames.length > 0 ? newState.filteredAcquisitionDealNames : this.props.dealNames;
      newState.filteredAcquisitionDealNames = filterDealNamesByCompany(acquisitionDealNames, this.state.viewingDealsOfValue);

      const acquisitionDeals = newState.filteredAcquisitionDeals && newState.filteredAcquisitionDeals.length > 0 ? newState.filteredAcquisitionDeals : this.props.deals;
      newState.filteredAcquisitionDeals = filterDealNamesByCompany(acquisitionDeals, this.state.viewingDealsOfValue);
    }

    if (hasSelectedFilter && this.state.selectedFilter.group.id !== 0) {
      newState.selectedFund = this.state.selectedFilter.group.id;
    }

    if (!this.getShouldShowAll() && hasSelectedFilter) {
      this.props.getScreenPreferences({ screenType: fundViewMetric, fundId: this.state.selectedFilter.group.id});
    }  else {
      this.props.getScreenPreferences({ screenType: portfolioViewMetric });
    }


    this.setState({ ...this.state, ...newState });
  }

  handleSearchChange = (event) => this.setState({ searchText: event.target.value });
  handleMetricSearchChange = (event) => this.setState({ metricSearchText: event.target.value });

  closeModal = () => {
    return this.setState(
      {
        openModal:
        {
          name: null, data: null
        }
      }
    );
  }

  // Fund Modal functions
  updateFund = (fund) => this.props.updateDashboardFund({ fund, callback: () => this.props.getDashboardFundNames() });
  createFund = (fund) => this.props.postDashboardFund({ fund, callback: () => this.props.getDashboardFundNames() });
  openFundModalNew = () => this.setState({ openModal: { name: FUND_MODAL, data: null } });
  openFundModalEdit = (data) => this.setState({ openModal: { name: FUND_MODAL, data } });
  renderFundModal = () => {
    const isOpen = this.state.openModal && this.state.openModal.name === FUND_MODAL;
    if (!isOpen) return null;

    return (
      <FundModal
        isOpen={isOpen}
        isEdit={!!this.state.openModal.data}
        onSave={this.createFund}
        updateFund={this.updateFund}
        onClose={this.closeModal}
        fund={this.state.openModal.data}
        authentication={this.props.authentication}
      />
    );
  }

  // Deal Modal Functions
  createDeal = (deal) => {
    deal.dealStatusId = this.state.dealStatus;
    deal.stageId = null;
    deal.properties = [];
    deal.isPipeline = false;
    deal.fundId = deal.dealGroupId;
    deal.interestRate = 0;

    return this.props.postDashboardDeal({
      deal,
      callback: () => {
        this.props.getDealNames();
      }
    });
  }

  openNewDealModal = (dealStatus) => this.setState({ openModal: { name: DEAL_MODAL, data: null }, dealStatus });

  renderDealModal = () => {
    const isOpen = this.state.openModal && this.state.openModal.name === DEAL_MODAL;
    if (!isOpen) return null


    return (
      <DealModal
        isOpen={isOpen}
        onSave={this.createDeal}
        onClose={this.closeModal}
        deal={this.state.openModal.data}
        authentication={this.props.authentication}
        dealGroups={this.props.funds}
        sponsorCompanies={this.props.companies.companySponsorCompanies}
        hasBrokerAccess={this.hasBrokerAccess()}
      />
    );
  }

  // DealPdfSummaryModal Dialog
  openDealPdfSummaryModal = () => {
    this.props.getMaxDealsInPdfSummary();
    this.setState({ openModal: { name: DEAL_PDF_SUMMARY_MODAL, data: null } });
  };
  renderDealPdfSummaryModal() {
    const isOpen = this.state.openModal && this.state.openModal.name === DEAL_PDF_SUMMARY_MODAL;
    if (!isOpen || !this.props.maxDealsInPdfSummary) return null;

    return (
      <DealPdfSummaryModal
        isOpen={isOpen}
        onClose={this.closeModal}
        deals={this.state.filteredDealNames}
        isLoading={this.props.isDownloading}
        onDownload={this.props.getDashboardDealPdfSummary}
        maxDealsInPdfSummary={this.props.maxDealsInPdfSummary}
      />
    );
  }

  getChildDealsFilter = () => {
    const { dealCompanies } = this.props;
    const { viewingDealsOfValue } = this.state;

    return dealCompanies && dealCompanies.length > 2 ? (
      <InputSimpleDropdown
        name={"childDealsFilter"}
        value={viewingDealsOfValue}
        label={"Viewing Deals Of"}
        options={dealCompanies}
        onChange={(value) => this.setState({ viewingDealsOfValue: value })} />
    ) : null;
  }

  //overwrite payload for updateScreenPreferences
  updateScreenPreferences = (values) => {
    const payload = Object.assign({}, values);

    if (values.screenType === fundViewMetric && this.state.selectedFund) {
      payload.fundId = this.state.selectedFund;
      payload.callback = () => this.props.getScreenPreferences({ screenType: fundViewMetric, fundId: this.state.selectedFilter.group.id});
    }
    this.props.updateContactScreenPreferences(payload);
  };


  // render commands

  renderBody = () => {
    const { selectedFilter } = this.state;
    const toggleButtonKey = 0;
    const dashboardKey = selectedFilter && selectedFilter.userDashboardType || 0;
    let filterName = selectedFilter && selectedFilter.group && selectedFilter.group.name;
    filterName = !filterName ? defaultAssetManagementGroups[0].name : filterName;
    const key = `${dashboardKey}-${toggleButtonKey}`;

    const redirectToDealPage = async (dealId) => {
      if (this.props.flags.bar2389EnableNewDealPage) {
        const newFrontendUrl = await getEnvConfig("newFrontendUrl");
        const dealIdGuid = convertIntegerToGUID(dealId);
        window.location.assign(`${newFrontendUrl}/deals/${dealIdGuid}`);
      } else {
        this.props.history.push(`/deal/${dealId}`);
      }
    };

    switch (key) {
      // acquisitions management - deal metric view
      // 0-1 not available!

      // asset management - deal metric view
      case '0-2':
        return (
          <AssetDealMetric
            title={filterName}
            metricData={this.getMetrics()}
            screenPreferences={this.getScreenPreferences()}
            screenType={this.getScreenType()}
            onSearchChange={this.handleMetricSearchChange}
            searchText={this.state.metricSearchText}
            menuOptions={this.getMenuOptions()}
            onModalSave={(values) => this.updateScreenPreferences(values)}
            childDealsFilter={this.getChildDealsFilter()} />
        );

      // acquisitions management - list view
      case '1-1':
        return (
          <AcquisitionsDataTable
            title={filterName}
            items={this.state.filteredAcquisitionDeals}
            onSearchChange={this.handleSearchChange}
            searchText={this.state.searchText}
            menuOptions={this.getMenuOptions()}
            onRowClick={(item) => redirectToDealPage(item.id)}
            childDealsFilter={this.getChildDealsFilter()}
          />
        );

      // asset management - list view
      case '0-1':
        return (
          <AssetDataTable
            title={filterName}
            items={this.state.filteredDeals}
            onSearchChange={this.handleSearchChange}
            searchText={this.state.searchText}
            menuOptions={this.getMenuOptions()}
            onRowClick={(item) => redirectToDealPage(item.id)}
            childDealsFilter={this.getChildDealsFilter()}
          />
        );

      // acquisitions management - photos
      case '1-0':
        return (
          <AcquisitionsImageGrid
            {...this.props}
            title={filterName}
            items={this.state.filteredAcquisitionDealNames}
            onSearchChange={this.handleSearchChange}
            searchText={this.state.searchText}
            menuOptions={this.getMenuOptions()}
            childDealsFilter={this.getChildDealsFilter()}
            redirectToDealPage={redirectToDealPage}
          />
        );

      // asset management - photos
      case '0-0':
      default:
        return (
          <AssetImageGrid
            {...this.props}
            title={filterName}
            items={this.state.filteredDealNames}
            onSearchChange={this.handleSearchChange}
            searchText={this.state.searchText}
            menuOptions={this.getMenuOptions()}
            childDealsFilter={this.getChildDealsFilter()}
            redirectToDealPage={redirectToDealPage}
          />
        );
    }
  }

  determineHeroCardOptions = () => {
    if (this.hasBrokerAccess()) return this.state.sponsorCompaniesList;
    return getContactSettingAsInt(DASHBOARD_CONFIG_ITEM, DEFAULT_DASHBOARD_SELECTION) ? this.state.acquisitionsDevelopmentGroups : this.state.assetManagementGroups;
  }

  renderHeroCard = () => {
    const { authentication } = this.props;
    const { zep104HideAcquisitionsAndDevelopmentFeature: acqAndDevFeature } = this.props.flags;

    let company = this.props.company;
    if (this.props.authentication.isGuest) {
      company = this.props.companies.company;
    }

    if (!acqAndDevFeature || !company) {
      return null;
    }

    const showAcqAndDev =
      typeof acqAndDevFeature[0] === "number"
        ? acqAndDevFeature.includes(company.id)
        : true;

    const acqAndDevSelection = 1;

    const userDashboardType = getContactSettingAsInt(DASHBOARD_CONFIG_ITEM, DEFAULT_DASHBOARD_SELECTION);

    if (!showAcqAndDev && userDashboardType === acqAndDevSelection) {
      setContactSetting(DASHBOARD_CONFIG_ITEM, DEFAULT_DASHBOARD_SELECTION, true);
      this.props.hardReload();
    }

    return (
      <UserDashboardHeroCard
        enableChangeDialog={!authentication.isGuest}
        defaultCompanyImage={defaultImageUrl}
        companyImage={company.logoImage}
        companyTitle={company.name}
        assetManagementGroups={this.determineHeroCardOptions()}
        acquisitionsDevelopmentGroups={this.determineHeroCardOptions()}
        userDashboardType={userDashboardType}
        onEditGroup={(group) => this.openFundModalEdit(group)}
        onChange={(selectedFilter) => this.setState({ selectedFilter: { ...selectedFilter } })}
        onUpdateAssetData={() => this.props.history.push('/properties')}
        onCreateNewDeal={() => this.openNewDealModal(DEAL_STATUSES.PIPELINE)}
        shouldShowAandDButton={showAcqAndDev}
        onImageRemove={() => this.props.deleteCompanyLogoImage({ companyId: company.id })}
        onImageChange={file => this.props.postCompanyLogoImage({ companyId: company.id, file })}
        isBroker={this.hasBrokerAccess()}
        brokerNavigation={() => this.props.history.push('/sreoScreener')}
      />
    );
  }

  render() {
    const { authentication } = this.props;
    return (
      <>
        <CardPageLayout {...this.props}
          bannerImageUrl={this.props.company && this.props.company.bannerImage}
          cardComponent={this.renderHeroCard()}
          bodyComponent={this.renderBody()}
          enableImageChangeDialog={!authentication.isGuest}
          onImageRemove={() => this.props.deleteCompanyBannerImage({ companyId: this.props.company.id })}
          onImageChange={file => this.props.postCompanyBannerImage({ companyId: this.props.company.id, file })}
        />
        {this.renderDealModal()}
        {this.renderFundModal()}
        {this.renderDealPdfSummaryModal()}
      </>
    )
  }
}

const mapStateToProps = ({ dashboard, authentication, newScreenPreferences, newPortfolio, newFund, companies }) => {
  return ({
    ...dashboard
    , company: dashboard.company || mapCompanySelector(authentication)
    , companies
    , screenPreferences: newScreenPreferences.screen_pref || null
    , portfolioMetrics: newPortfolio.metricData
    , fundMetrics: newFund.metricData
    , authentication
  })
};

export default (compose(
  withRouter
  , withLDConsumer()
  , withStyles()
  , withHardReload
  , connect(mapStateToProps, {
    getDealNames
    , getDashboardDeals
    , getDashboardFundNames
    , getScreenPreferences
    , getPortfolioMetrics
    , getFundMetrics
    , updateContactScreenPreferences
    , updateDashboardFund
    , postDashboardFund
    , getDashboardDealPdfSummary
    , postDashboardDeal
    , postCompanyBannerImage
    , deleteCompanyBannerImage
    , postCompanyLogoImage
    , deleteCompanyLogoImage
    , getCompany
    , getCompanyForUser
    , getSponsorCompanies
    , getMaxDealsInPdfSummary
  })
)(Dashboard));
